import { useAppDispatch, useAppSelector } from "@app/hooks";
import { LCheckbox, LTableResults, LTableSearch, Loader } from "@components";
import { searchExistingUsersFilter } from "@fadmin/tabs/results/components/filterHelper";
import { getActiveMessage } from "@helpers/dateHelpers";
import { getRolesFromLocations } from "@helpers/organizationHelper";
import { useApi } from "@services/useApi";
import { ExistingUser, OrganizationUserStatus } from "@store/generalTypes";
import { updateStaffCounts } from "@store/organizationSlice";
import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import _ from "lodash";
import { ChangeEvent, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";

export const ManageStaff = (): JSX.Element => {
    const validateMethods = useForm();
    const dispatch = useAppDispatch();
    const { organization, orgLocations } = useAppSelector((x) => {
        return { organization: x.organization, orgLocations: x.orgLocations };
    });
    const { getOrganizationUsers, loading } = useApi();
    const [existingUsers, setExistingUsers] = useState<ExistingUser[]>([]);
    const [data, setData] = useState<ExistingUser[]>([]);

    const handleGetOrganizationUsers = async (): Promise<void> => {
        const orgUsers = await getOrganizationUsers(organization.id!);
        setExistingUsers(orgUsers.items);
        setData(orgUsers.items);
        dispatch(
            updateStaffCounts({
                active: orgUsers.items.filter(
                    (u) => u.status.toLowerCase() === OrganizationUserStatus.Active.toLowerCase()
                ).length,
                inactive:
                    orgUsers.items.filter((u) => u.status.toLowerCase() !== OrganizationUserStatus.Active.toLowerCase())
                        .length ?? 0
            })
        );
    };

    useEffect(() => {
        void handleGetOrganizationUsers();
    }, [organization]);

    const columnHelper = createColumnHelper<ExistingUser>();
    const baseColumns: Array<ColumnDef<ExistingUser, any>> = [
        {
            id: "select",
            header: ({ table }) => (
                <LCheckbox
                    classes="pl-1 pt-1"
                    inputName="checkAllOptions"
                    checked={table.getIsAllRowsSelected()}
                    onChange={table.getToggleAllRowsSelectedHandler()}
                />
            ),
            cell: ({ row }) => (
                <LCheckbox
                    classes="pl-1 pt-1"
                    value={row.original.id.toString()}
                    inputName={"row-" + row.original.id.toString()}
                    checked={row.getIsSelected()}
                    onChange={row.getToggleSelectedHandler()}
                />
            )
        },
        columnHelper.accessor("lastName", {
            header: () => "Last Name"
        }),
        columnHelper.accessor("firstName", {
            header: () => "First Name"
        }),
        columnHelper.accessor("email", {
            header: () => "Email"
        }),
        columnHelper.accessor("locations", {
            header: () => "Role(s)",
            cell: (cell) => {
                const locs = cell.row.original.locations;
                if (locs && locs.length > 0) {
                    const roles = getRolesFromLocations(locs);
                    if (roles && roles.length > 0) {
                        return roles.map((r, index) => (
                            <span className="tag is-info is-light mr-2" key={r ?? "" + index.toString()}>
                                {r}
                            </span>
                        ));
                    }
                }
                return "";
            }
        }),
        columnHelper.accessor("locations", {
            header: () => "Location(s)",
            cell: (cell) => {
                const orgLocationIds = orgLocations?.locations?.map((x) => x.id);
                const val = cell.row.original.locations;
                if (val && val.length > 0) {
                    const names = val.filter((x) => orgLocationIds?.includes(x.id)).map((v) => v.name);
                    return names.join(", ");
                }
            }
        }),
        columnHelper.accessor("status", {
            header: () => "Status",
            cell: (cell) => {
                return `${_.startCase(cell.getValue())} ${getActiveMessage(cell.row.original.lastActivity)}`;
            }
        }),
        {
            id: "menu",
            enableSorting: false,
            cell: (cell) => {
                return (
                    <></>
                    // <LDropdownMenu
                    //     id={`menu_${cell.row.original.id}`}
                    //     options={getOptions(() => console.log("click"))}
                    //     isRightAligned
                    //     type="tableRowMenu"
                    //     hideErrorLabel
                    //     theme="light"
                    // />
                );
            }
        }
    ];

    const handleSearch = (e: ChangeEvent<HTMLInputElement>): void => {
        setData(searchExistingUsersFilter(e.target.value, existingUsers));
    };

    return (
        <FormProvider {...validateMethods}>
            <LTableSearch
                numRecords={data.length}
                label="Search staff"
                handleSearch={handleSearch}
                showInactive={true}
                inactiveCount={organization.inactiveStaffUsers}
                resultCountContainerSize="4"
            />
            {LTableResults<ExistingUser>(data, baseColumns)}
            {loading && <Loader />}
        </FormProvider>
    );
};

// const getOptions = (handler: () => void): DropdownMenuOption[] => [
//     {
//         optionName: "Resend Invite",
//         children: (
//             <div className="my-2">
//                 <a
//                     className="has-text-primary-blue has-text-weight-bold"
//                     onClick={() => {
//                         handler();
//                     }}
//                 >
//                     Resend invitation
//                 </a>
//             </div>
//         )
//     },
//     {
//         optionName: "Reset Password",
//         children: (
//             <div className="my-2">
//                 <a
//                     className="has-text-primary-blue has-text-weight-bold"
//                     onClick={() => {
//                         handler();
//                     }}
//                 >
//                     Reset Password
//                 </a>
//             </div>
//         )
//     },
//     {
//         optionName: "Edit user details",
//         children: (
//             <div className="my-2">
//                 <a
//                     className="has-text-primary-blue has-text-weight-bold mr-5"
//                     onClick={() => {
//                         handler();
//                     }}
//                 >
//                     Edit user details
//                 </a>
//             </div>
//         )
//     },
//     {
//         optionName: "Delete user",
//         children: (
//             <div className="my-2">
//                 <a
//                     className="has-text-danger has-text-weight-bold"
//                     onClick={() => {
//                         handler();
//                     }}
//                 >
//                     Delete user
//                 </a>
//             </div>
//         )
//     }
// ];
